import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import styles from './DriverCabinet.module.scss';
import Notification from 'utils/Notifications';
import {
  getDriverCabinet,
  resubmitDriverChecked,
  createBaseRequest,
  getDriverMyLoads,
  createDocChangeRequest,
  getMyDocChangeRequests,
  getDriverCheckedMy,
} from 'service/DriverRegistrationService';
import {
  getTopBases,
  getChpps,
  getPrivatePoints,
} from 'service/AdminPanelService';

const DriverCabinet = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  // Base selection
  const [bases, setBases] = useState({ topbases: [], chpps: [], privatepoints: [] });
  const [basesLoading, setBasesLoading] = useState(false);
  const [selectedBaseType, setSelectedBaseType] = useState('topbase');
  const [selectedBaseId, setSelectedBaseId] = useState(null);
  const [submittingBaseRequest, setSubmittingBaseRequest] = useState(false);

  // Change base modal
  const [showChangeBase, setShowChangeBase] = useState(false);
  const [changeSelectedBaseType, setChangeSelectedBaseType] = useState('topbase');
  const [changeSelectedBaseId, setChangeSelectedBaseId] = useState(null);

  // Resubmit
  const [resubmitting, setResubmitting] = useState(false);

  // My docs
  const [showMyDocs, setShowMyDocs] = useState(false);
  const [myDcRecord, setMyDcRecord] = useState(null);
  const [docChangeTarget, setDocChangeTarget] = useState(null);
  const [docChangeFile, setDocChangeFile] = useState(null);
  const [docChangePreviewUrl, setDocChangePreviewUrl] = useState(null);
  const [submittingDocChange, setSubmittingDocChange] = useState(false);
  const [myDocReqs, setMyDocReqs] = useState([]);
  const [docPreviewOpen, setDocPreviewOpen] = useState(null);

  // Loads
  const [loads, setLoads] = useState(null);
  const [loadsLoading, setLoadsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  const loadCabinet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDriverCabinet();
      setData(res.data);
    } catch (e) {
      setNotify({ isOpen: true, message: 'Ошибка загрузки данных', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCabinet();
  }, [loadCabinet]);

  const loadMyLoads = useCallback(async (page = 1) => {
    setLoadsLoading(true);
    try {
      const res = await getDriverMyLoads({ page, page_size: 10 });
      setLoads(res.data);
    } catch (e) {
      console.error('Failed to load driver loads', e);
    } finally {
      setLoadsLoading(false);
    }
  }, []);

  const loadBases = useCallback(async () => {
    setBasesLoading(true);
    try {
      const [tb, ch, pp] = await Promise.all([getTopBases(), getChpps(), getPrivatePoints()]);
      const HIDDEN = ['\u0427\u0435\u0440\u043d\u044b\u0439 \u0441\u043f\u0438\u0441\u043e\u043a'];
      const filterHidden = (list) => list.filter(b => !HIDDEN.includes(b.name));
      setBases({
        topbases: filterHidden(tb.data?.results || tb.data || []),
        chpps: filterHidden(ch.data?.results || ch.data || []),
        privatepoints: filterHidden(pp.data?.results || pp.data || []),
      });
    } catch (e) {
      console.error('Failed to load bases', e);
    } finally {
      setBasesLoading(false);
    }
  }, []);

  const loadMyDocReqs = useCallback(async () => {
    try {
      const res = await getMyDocChangeRequests();
      setMyDocReqs(res.data?.results || res.data || []);
    } catch (e) {
      console.error('Failed to load doc change requests', e);
    }
  }, []);

  const loadMyDc = useCallback(async () => {
    try {
      const res = await getDriverCheckedMy();
      setMyDcRecord(res.data || null);
    } catch (e) {
      console.error('Failed to load driver_checked', e);
    }
  }, []);

  useEffect(() => {
    if (data?.driver && !data?.driver?.top_base && !data?.driver?.chpp && !data?.driver?.private_point) {
      loadBases();
    }
    if (showChangeBase) {
      loadBases();
    }
  }, [data, showChangeBase, loadBases]);

  useEffect(() => {
    if (data?.driver) {
      loadMyLoads(historyPage);
    }
  }, [data?.driver, historyPage, loadMyLoads]);

  useEffect(() => {
    if (data?.driver) {
      loadMyDocReqs();
      if (!data?.driver_checked) {
        loadMyDc();
      }
    }
  }, [data?.driver, data?.driver_checked, loadMyDocReqs, loadMyDc]);

  const handleSubmitJoinRequest = async () => {
    if (!selectedBaseId) {
      setNotify({ isOpen: true, message: 'Выберите базу', type: 'warning' });
      return;
    }
    setSubmittingBaseRequest(true);
    try {
      const payload = { request_type: 'join' };
      if (selectedBaseType === 'topbase') payload.top_base = selectedBaseId;
      else if (selectedBaseType === 'chpp') payload.chpp = selectedBaseId;
      else payload.private_point = selectedBaseId;
      await createBaseRequest(payload);
      setNotify({ isOpen: true, message: 'Заявка отправлена! Ожидайте ответа оператора.', type: 'success' });
      await loadCabinet();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setSubmittingBaseRequest(false);
    }
  };

  const handleSubmitLeaveRequest = async () => {
    if (!changeSelectedBaseId) {
      setNotify({ isOpen: true, message: 'Выберите новую базу', type: 'warning' });
      return;
    }
    setSubmittingBaseRequest(true);
    try {
      const payload = { request_type: 'leave' };
      if (changeSelectedBaseType === 'topbase') payload.new_top_base = changeSelectedBaseId;
      else if (changeSelectedBaseType === 'chpp') payload.new_chpp = changeSelectedBaseId;
      else payload.new_private_point = changeSelectedBaseId;
      await createBaseRequest(payload);
      setNotify({ isOpen: true, message: 'Запрос на смену базы отправлен.', type: 'success' });
      setShowChangeBase(false);
      await loadCabinet();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setSubmittingBaseRequest(false);
    }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      const fd = new FormData();
      await resubmitDriverChecked(fd);
      setNotify({ isOpen: true, message: 'Заявка повторно отправлена на рассмотрение.', type: 'success' });
      await loadCabinet();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setResubmitting(false);
    }
  };

  const handleDocChangeSubmit = async () => {
    if (!docChangeFile || !docChangeTarget) return;
    setSubmittingDocChange(true);
    try {
      const fd = new FormData();
      fd.append('field_name', docChangeTarget.field);
      fd.append('field_label', docChangeTarget.label);
      fd.append('new_file', docChangeFile);
      await createDocChangeRequest(fd);
      setNotify({ isOpen: true, message: 'Заявка на смену документа отправлена. Ожидайте подтверждения.', type: 'success' });
      setDocChangeTarget(null);
      setDocChangeFile(null);
      setDocChangePreviewUrl(null);
      await loadCabinet();
      await loadMyDocReqs();
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка при отправке заявки', type: 'error' });
    } finally {
      setSubmittingDocChange(false);
    }
  };

  if (loading) return <div className={styles.loadingState}>Загрузка личного кабинета...</div>;
  if (!data) return null;

  const fmtDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const dc = data.driver_checked || myDcRecord;
  const driver = data.driver;
  const pendingBR = data.pending_base_request;

  const fio = (dc?.last_name && dc?.first_name)
    ? `${dc.last_name} ${dc.first_name}${dc.middle_name ? ' ' + dc.middle_name : ''}`
    : driver
    ? `${driver.employee_name || ''}`
    : 'Водитель';

  const isBlocked = driver?.status === 'Заблокирован';
  const isBusy = driver?.status === 'В погрузке' || driver?.status === 'В пути';
  const isFree = driver?.status === 'Свободен';
  const isUnderDocReview = driver?.status === 'На рассмотрении';

  const DOC_FIELDS = [
    { field: 'photo_vehicle',       label: 'Фото ТС',        url: driver?.photo_vehicle_url       || dc?.photo_vehicle_url },
    { field: 'passport_front',      label: 'Паспорт (л)',     url: driver?.passport_front_url      || dc?.passport_front_url },
    { field: 'passport_back',       label: 'Паспорт (о)',     url: driver?.passport_back_url       || dc?.passport_back_url },
    { field: 'license_front',       label: 'Права (л)',       url: driver?.license_front_url       || dc?.license_front_url },
    { field: 'license_back',        label: 'Права (о)',       url: driver?.license_back_url        || dc?.license_back_url },
    { field: 'tech_passport_front', label: 'Тех.пасп. (л)',  url: driver?.tech_passport_front_url || dc?.tech_passport_front_url },
    { field: 'tech_passport_back',  label: 'Тех.пасп. (о)',  url: driver?.tech_passport_back_url  || dc?.tech_passport_back_url },
  ];

  const currentBase = driver?.top_base_name || driver?.chpp_name || driver?.private_point_name;
  const currentBaseType = driver?.top_base ? 'ТОП-База' : driver?.chpp ? 'ТЭЦ' : driver?.private_point ? 'Соц. объект / МСУ' : null;

  const getDriverBadgeClass = (st) => {
    if (!st) return styles.badge;
    if (st === 'Свободен') return `${styles.badge} ${styles.badgeFree}`;
    if (st === 'Заблокирован') return `${styles.badge} ${styles.badgeBlocked}`;
    if (st === 'На рассмотрении') return `${styles.badge} ${styles.badgePending}`;
    return `${styles.badge} ${styles.badgeLoading}`;
  };

  const currentBases = selectedBaseType === 'topbase' ? bases.topbases
    : selectedBaseType === 'chpp' ? bases.chpps
    : bases.privatepoints;

  const changeBases = changeSelectedBaseType === 'topbase' ? bases.topbases
    : changeSelectedBaseType === 'chpp' ? bases.chpps
    : bases.privatepoints;

  return (
    <div className={styles.cabinet}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.avatarPlaceholder}>
            {(dc?.last_name?.[0] || driver?.employee_name?.[0] || '?').toUpperCase()}
          </div>
          <div className={styles.headerInfo}>
            <h2 className={styles.userName}>{fio}</h2>
            <p className={styles.userInn}>UID: {driver?.uid || driver?.qrcode_scr || dc?.inn || '—'}</p>
            {driver && (
              <span className={getDriverBadgeClass(driver.status)}>
                {driver.status || 'Свободен'}
              </span>
            )}
            {!driver && dc && (
              <span className={`${styles.badge} ${dc.status === 'pending' ? styles.badgePending : dc.status === 'approved' ? styles.badgeApproved : styles.badgeRejected}`}>
                {dc.status === 'pending' ? '⏳ На рассмотрении'
                  : dc.status === 'approved' ? '✓ Одобрено'
                  : '✗ Отказано'}
              </span>
            )}
          </div>
        </div>
        <p className={styles.systemTitle}>Центр управления и контроля логистики ОАО "Кыргызкомур"</p>
      </div>

      {/* ── QR Pass ── */}
      {driver?.uid && currentBase && !isUnderDocReview && (
        <div className={styles.card} style={{ textAlign: 'center' }}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>🪪</span>
            QR-пропуск
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
            Предъявите QR-код охраннику или оператору для отметки
          </p>
          <div style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <QRCodeSVG value={driver.uid} size={180} level="M" />
          </div>
          <p style={{ marginTop: 12, fontSize: 11, color: '#9ca3af', letterSpacing: '0.05em', wordBreak: 'break-all' }}>{driver.uid}</p>
        </div>
      )}

      {/* ── Registration status ── */}
      {dc && dc.status === 'pending' && !driver && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>⏳</span>
            Заявка на рассмотрении
          </div>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
            Ваша заявка отправлена в администрацию ОАО "Кыргызкомур". После проверки документов
            и данных вы получите доступ к полному функционалу. Пожалуйста, ожидайте подтверждения.
          </p>
        </div>
      )}

      {/* ── Rejected ── */}
      {dc && dc.status === 'rejected' && !driver && (
        <>
          <div className={styles.rejectedBlock}>
            <p className={styles.rejectedTitle}>✗ Заявка отклонена</p>
            <p className={styles.rejectedReason}>
              <strong>Причина:</strong> {dc.rejection_reason || 'Причина не указана'}
            </p>
            <p className={styles.rejectedContact}>
              При возникновении вопросов обратитесь по номеру: <strong>0600011032</strong>
            </p>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <span className={styles.cardTitleIcon}>🔄</span>
              Повторная подача
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              Вы можете исправить данные и отправить заявку повторно.
            </p>
            <button
              className={styles.btnPrimary}
              onClick={handleResubmit}
              disabled={resubmitting}
            >
              {resubmitting ? 'Отправка...' : 'Отправить повторно'}
            </button>
          </div>
        </>
      )}

      {/* ── Driver profile info ── */}
      {driver && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>👤</span>
            Профиль водителя
          </div>
          <div className={styles.infoGrid}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>ФИО</span>
              <span className={styles.infoValue}>{driver.employee_name || fio}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Телефон</span>
              <span className={styles.infoValue}>{driver.phone || dc?.phone || '—'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Гос. номер</span>
              <span className={styles.infoValue}>{driver.vehicle_number || '—'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Марка / модель</span>
              <span className={styles.infoValue}>{driver.vehicle_model || '—'}</span>
            </div>
          </div>
          {!isBlocked && !isBusy && (
            <div className={styles.actionsRow} style={{ marginTop: 12 }}>
              <button
                className={styles.btnSecondary}
                onClick={() => { setShowMyDocs(d => !d); setDocPreviewOpen(null); }}
              >
                📄 {showMyDocs ? 'Скрыть документы' : 'Мои документы'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Under doc review notice ── */}
      {driver && isUnderDocReview && (
        <div className={styles.card} style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>⏳</span>
            Заявка на смену документа
          </div>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
            Одна или несколько заявок на смену документов отправлены на рассмотрение.
            QR-пропуск временно недоступен. Ожидайте подтверждения администратором.
            Остальные документы вы можете продолжать подавать заявки на смену.
          </p>
        </div>
      )}

      {/* ── My documents section ── */}
      {driver && !isBlocked && !isBusy && showMyDocs && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>📄</span>
            Мои документы
          </div>
          {!dc ? (
            <p style={{ fontSize: 13, color: '#9ca3af' }}>Загрузка документов...</p>
          ) : (() => {
            const pendingByField = {};
            myDocReqs.filter(r => r.status === 'pending').forEach(r => { pendingByField[r.field_name] = r; });
            return (
              <>
                {DOC_FIELDS.map(({ field, label, url }) => {
                  const pending = pendingByField[field];
                  return (
                    <div key={field} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>{label}</p>
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          {url ? (
                            <button
                              onClick={() => setDocPreviewOpen(docPreviewOpen === url ? null : url)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: docPreviewOpen === url ? '1.5px solid #374151' : '1px solid #d1d5db', background: docPreviewOpen === url ? '#f0f4ff' : '#fff', color: '#374151', cursor: 'pointer', fontSize: 12 }}>
                              📎 Текущий
                            </button>
                          ) : (
                            <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Не загружен</span>
                          )}
                          {pending && (
                            <>
                              <span style={{ fontSize: 12, color: '#94a3b8' }}>→</span>
                              <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>⏳ На рассмотрении</span>
                              <button
                                onClick={() => setDocPreviewOpen(docPreviewOpen === pending.new_file_url ? null : pending.new_file_url)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: docPreviewOpen === pending.new_file_url ? '1.5px solid #92400e' : '1px solid #fde68a', background: docPreviewOpen === pending.new_file_url ? '#fef9c3' : '#fffbeb', color: '#92400e', cursor: 'pointer', fontSize: 12 }}>
                                📎 Новый
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {!isBlocked && !isBusy && !pending && (
                        <button
                          onClick={() => { setDocChangeTarget({ field, label, currentUrl: url }); setDocChangeFile(null); setDocChangePreviewUrl(null); }}
                          style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: url ? '1.5px solid #0d6efd' : '1.5px solid #16a34a', background: '#fff', color: url ? '#0d6efd' : '#16a34a', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {url ? '🔄 Сменить' : '📤 Загрузить'}
                        </button>
                      )}
                    </div>
                  );
                })}
                {docPreviewOpen && (
                  <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: '#f1f5f9' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Просмотр</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <a href={docPreviewOpen} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0d6efd', textDecoration: 'none' }}>Открыть ↗</a>
                        <button onClick={() => setDocPreviewOpen(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16 }}>✕</button>
                      </div>
                    </div>
                    {docPreviewOpen.toLowerCase().includes('.pdf') ? (
                      <iframe src={docPreviewOpen} title="preview" style={{ width: '100%', height: 400, border: 'none', display: 'block' }} />
                    ) : (
                      <img src={docPreviewOpen} alt="preview" style={{ width: '100%', maxHeight: 400, objectFit: 'contain', display: 'block' }} />
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ── Blocked ── */}
      {isBlocked && (
        <div className={styles.rejectedBlock}>
          <p className={styles.rejectedTitle}>🔒 Аккаунт заблокирован</p>
          <p className={styles.rejectedReason}>
            Ваш аккаунт был заблокирован. Обратитесь к администратору.
          </p>
          <p className={styles.rejectedContact}>Контакт: <strong>0600011032</strong></p>
        </div>
      )}

      {/* ── Pending base request ── */}
      {driver && pendingBR && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>📋</span>
            Активная заявка
          </div>
          <div className={styles.pendingRequestBlock}>
            <div className={styles.pendingRequestIcon}>
              {pendingBR.request_type === 'join' ? '🏭' : '🔄'}
            </div>
            <div className={styles.pendingRequestText}>
              <p className={styles.pendingRequestTitle}>
                {pendingBR.request_type === 'join'
                  ? `Запрос на вступление в "${pendingBR.base_name || '—'}"`
                  : `Запрос на выход из "${pendingBR.base_name || '—'}"`}
              </p>
              <p className={styles.pendingRequestSub}>
                {pendingBR.request_type === 'leave' && pendingBR.new_base_name
                  ? `Новая база: ${pendingBR.new_base_name}`
                  : 'Ожидание подтверждения оператора'}
              </p>
            </div>
            <span className={`${styles.badge} ${styles.badgePending}`}>На рассмотрении</span>
          </div>
        </div>
      )}

      {/* ── Assigned base ── */}
      {driver && !isBlocked && currentBase && !pendingBR && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>🏭</span>
            Текущая база
          </div>
          <div className={styles.baseInfoBlock}>
            <div className={styles.baseInfoIcon}>🏗️</div>
            <div>
              <p className={styles.baseInfoName}>{currentBase}</p>
              <p className={styles.baseInfoType}>{currentBaseType}</p>
            </div>
          </div>
          {!isBusy && isFree && (
            <div className={styles.actionsRow}>
              <button
                className={styles.btnSecondary}
                onClick={() => setShowChangeBase(true)}
              >
                🔄 Сменить базу
              </button>
            </div>
          )}
          {isBusy && (
            <p style={{ fontSize: 13, color: '#92400e', marginTop: 8 }}>
              Смена базы недоступна во время рейса ({driver.status}).
            </p>
          )}
        </div>
      )}

      {/* ── Base selection (approved but no base yet) ── */}
      {driver && !isBlocked && !currentBase && !pendingBR && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>🏭</span>
            Выберите базу для работы
          </div>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
            Ваш профиль подтверждён. Выберите базу, куда хотите подать заявку на работу.
          </p>
          <div className={styles.baseSelector}>
            <div className={styles.baseTabs}>
              {[{ key: 'topbase', label: 'ТОП-Базы' }, { key: 'chpp', label: 'ТЭЦ' }, { key: 'privatepoint', label: 'Соц. объекты / МСУ' }].map(t => (
                <button
                  key={t.key}
                  className={`${styles.baseTab} ${selectedBaseType === t.key ? styles.baseTabActive : ''}`}
                  onClick={() => { setSelectedBaseType(t.key); setSelectedBaseId(null); }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {basesLoading ? (
              <div className={styles.loadingState}>Загрузка...</div>
            ) : (
              <div className={styles.baseList}>
                {currentBases.length === 0 ? (
                  <div className={styles.emptyState}>Нет доступных баз</div>
                ) : currentBases.map(b => (
                  <div
                    key={b.id}
                    className={`${styles.baseItem} ${selectedBaseId === b.id ? styles.baseItemSelected : ''}`}
                    onClick={() => setSelectedBaseId(b.id)}
                  >
                    <div className={`${styles.baseItemDot} ${selectedBaseId === b.id ? styles.baseItemDotSelected : ''}`} />
                    <span className={styles.baseItemName}>{b.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.actionsRow}>
              <button
                className={styles.btnPrimary}
                onClick={handleSubmitJoinRequest}
                disabled={!selectedBaseId || submittingBaseRequest}
              >
                {submittingBaseRequest ? 'Отправка...' : 'Подать заявку'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Loads section ── */}
      {driver && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>🚛</span>
            Погрузки
          </div>

          {/* Active load */}
          {loadsLoading && !loads && (
            <p style={{ fontSize: 13, color: '#9ca3af' }}>Загрузка...</p>
          )}
          {loads && loads.active && loads.active.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', margin: '0 0 8px', fontWeight: 600 }}>Активная погрузка</p>
              {loads.active.map(l => (
                <div key={l.id} style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 16px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#15803d' }}>{l.status}</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(l.time_a)}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                    {[
                      ['База', l.top_base_name || l.chpp_name || l.private_point_name || '—'],
                      ['Гос. номер', l.vehicle_number || '—'],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600, color: '#111827' }}>{val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {loads && loads.active && loads.active.length === 0 && (
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>Нет активных погрузок</p>
          )}

          {/* History toggle */}
          <button
            onClick={() => { setShowHistory(h => !h); if (!showHistory && historyPage !== 1) setHistoryPage(1); }}
            style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', width: '100%', textAlign: 'left' }}
          >
            {showHistory ? '▲ Скрыть историю' : `▼ История погрузок${loads?.history_count ? ` (${loads.history_count})` : ''}`}
          </button>

          {showHistory && (
            <div style={{ marginTop: 12 }}>
              {loadsLoading ? (
                <p style={{ fontSize: 13, color: '#9ca3af' }}>Загрузка...</p>
              ) : !loads?.history || loads.history.length === 0 ? (
                <p style={{ fontSize: 13, color: '#9ca3af' }}>История пуста</p>
              ) : (
                <>
                  {loads.history.map(l => (
                    <div key={l.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>{l.top_base_name || l.chpp_name || l.private_point_name || '—'}</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(l.time_b)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {l.duration_str && <span style={{ fontSize: 12, color: '#6b7280' }}>⏱ {l.duration_str}</span>}
                        {l.vehicle_number && <span style={{ fontSize: 12, color: '#6b7280' }}>🚗 {l.vehicle_number}</span>}
                      </div>
                    </div>
                  ))}
                  {/* Pagination */}
                  {loads.history_count > 10 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                      <button
                        onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        disabled={historyPage === 1 || loadsLoading}
                        style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
                      >← Назад</button>
                      <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>Стр. {historyPage}</span>
                      <button
                        onClick={() => setHistoryPage(p => p + 1)}
                        disabled={historyPage * 10 >= loads.history_count || loadsLoading}
                        style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
                      >Вперёд →</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Change base modal ── */}      {showChangeBase && (
        <div className={styles.changeBaseOverlay} onClick={(e) => e.target === e.currentTarget && setShowChangeBase(false)}>
          <div className={styles.changeBaseModal}>
            <h3 className={styles.changeBaseTitle}>Смена базы</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
              Запрос будет отправлен в вашу текущую базу для подтверждения.
            </p>
            <div className={styles.baseTabs}>
              {[{ key: 'topbase', label: 'ТОП-Базы' }, { key: 'chpp', label: 'ТЭЦ' }, { key: 'privatepoint', label: 'Соц. объекты / МСУ' }].map(t => (
                <button
                  key={t.key}
                  className={`${styles.baseTab} ${changeSelectedBaseType === t.key ? styles.baseTabActive : ''}`}
                  onClick={() => { setChangeSelectedBaseType(t.key); setChangeSelectedBaseId(null); }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className={styles.baseList} style={{ marginTop: 10 }}>
              {basesLoading ? (
                <div className={styles.loadingState}>Загрузка...</div>
              ) : changeBases.filter(b => {
                // exclude current base
                if (driver?.top_base && selectedBaseType === 'topbase' && b.id === driver.top_base) return false;
                return true;
              }).map(b => (
                <div
                  key={b.id}
                  className={`${styles.baseItem} ${changeSelectedBaseId === b.id ? styles.baseItemSelected : ''}`}
                  onClick={() => setChangeSelectedBaseId(b.id)}
                >
                  <div className={`${styles.baseItemDot} ${changeSelectedBaseId === b.id ? styles.baseItemDotSelected : ''}`} />
                  <span className={styles.baseItemName}>{b.name}</span>
                </div>
              ))}
            </div>
            <div className={styles.changeBtnRow}>
              <button className={styles.btnSecondary} onClick={() => setShowChangeBase(false)} disabled={submittingBaseRequest}>Отмена</button>
              <button className={styles.btnPrimary} onClick={handleSubmitLeaveRequest} disabled={!changeSelectedBaseId || submittingBaseRequest}>
                {submittingBaseRequest ? 'Отправка...' : 'Отправить запрос'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Doc change modal ── */}
      {docChangeTarget && (
        <div className={styles.changeBaseOverlay} onClick={e => e.target === e.currentTarget && setDocChangeTarget(null)}>
          <div className={styles.changeBaseModal}>
            <h3 className={styles.changeBaseTitle}>{docChangeTarget.currentUrl ? 'Сменить документ' : 'Загрузить документ'}</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
              Тип: <strong>{docChangeTarget.label}</strong>
            </p>
            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>
              {docChangeTarget.currentUrl
                ? 'После отправки ваш QR-пропуск будет скрыт до подтверждения администратором.'
                : 'Загруженный документ будет отправлен на проверку администратору.'}
            </p>
            {docChangeTarget.currentUrl && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Текущий документ:</p>
                {docChangeTarget.currentUrl.toLowerCase().includes('.pdf') ? (
                  <a href={docChangeTarget.currentUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#0d6efd' }}>Открыть PDF ↗</a>
                ) : (
                  <img src={docChangeTarget.currentUrl} alt="current" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 8, display: 'block' }} />
                )}
              </div>
            )}
            <p style={{ margin: '0 0 6px', fontSize: 12, color: '#374151', fontWeight: 600 }}>{docChangeTarget.currentUrl ? 'Новый документ:' : 'Выберите файл:'}</p>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={e => {
                const file = e.target.files?.[0] || null;
                setDocChangeFile(file);
                if (file && file.type.startsWith('image/')) {
                  setDocChangePreviewUrl(URL.createObjectURL(file));
                } else {
                  setDocChangePreviewUrl(null);
                }
              }}
              style={{ display: 'block', marginBottom: 10, fontSize: 13, width: '100%' }}
            />
            {docChangePreviewUrl && (
              <img src={docChangePreviewUrl} alt="new preview" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, display: 'block' }} />
            )}
            {!docChangePreviewUrl && docChangeFile && (
              <p style={{ fontSize: 12, color: '#374151', marginBottom: 10 }}>📄 {docChangeFile.name}</p>
            )}
            <div className={styles.changeBtnRow}>
              <button className={styles.btnSecondary} onClick={() => { setDocChangeTarget(null); setDocChangeFile(null); setDocChangePreviewUrl(null); }} disabled={submittingDocChange}>Отмена</button>
              <button className={styles.btnPrimary} onClick={handleDocChangeSubmit} disabled={!docChangeFile || submittingDocChange}>
                {submittingDocChange ? 'Отправка...' : 'Подать заявку'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};

export default DriverCabinet;
